import React, { useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageTransitionOverlay, { PageTransitionHandle } from './PageTransitionOverlay';
import { prefetchRoute } from '../lib/routePrefetch';

export default function PageTransitionProvider({ children }: { children: React.ReactNode }) {
  const overlayRef = useRef<PageTransitionHandle>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isTransitioning = useRef(false);
  const mountedRef = useRef(false);
  const [blocking, setBlocking] = useState(false);

  // Reveal only after changing pages. The booking widget updates the hash;
  // revealing during cover would cancel its animation and prevent navigation.
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (!isTransitioning.current) return;

    (async () => {
      await overlayRef.current?.reveal();
      setBlocking(false);
      isTransitioning.current = false;
    })();
  }, [location.pathname]);

  // Intercept internal link clicks
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (isTransitioning.current) return;
      if (e.defaultPrevented) return;
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      if (e.button !== 0) return;

      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement | null;
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || !href.startsWith('/')) return;
      // Skip anchor links and admin routes
      if (href.includes('#') || href.startsWith('/admin')) return;

      const targetPath = href.split('?')[0];
      if (targetPath === location.pathname) return;

      e.preventDefault();
      e.stopPropagation();

      isTransitioning.current = true;
      setBlocking(true);

      overlayRef.current?.cover().then(() => {
        navigate(href);
      });
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [location.pathname, navigate]);

  // Prefetch lazy chunks dès qu'un lien interne est survolé/focus
  useEffect(() => {
    const handleIntent = (e: Event) => {
      const target = e.target as HTMLElement;
      const link = target.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!link) return;
      prefetchRoute(link.getAttribute('href'));
    };
    document.addEventListener('mouseover', handleIntent, { passive: true });
    document.addEventListener('focusin', handleIntent, { passive: true });
    document.addEventListener('touchstart', handleIntent, { passive: true });
    return () => {
      document.removeEventListener('mouseover', handleIntent);
      document.removeEventListener('focusin', handleIntent);
      document.removeEventListener('touchstart', handleIntent);
    };
  }, []);

  return (
    <>
      {children}
      {blocking && (
        <div className="fixed inset-0 pointer-events-auto" style={{ zIndex: 9998 }} aria-hidden="true" />
      )}
      <PageTransitionOverlay ref={overlayRef} />
    </>
  );
}
